import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SivaInnovationFundsDto, SivaReportingService } from 'src/api/v2';
import { SIVA_INNOVATION_FUNDS_KEY } from 'src/constants/hook-keys';
import { useAuth } from 'src/features/authorization';

export const useInnovationFunds = (year: number, clusterId: string) => {
  const [sivaReportingService] = useState(() => new SivaReportingService());
  const { checkAccess } = useAuth();
  const { t: tCommon } = useTranslation('common');
  const { write } = checkAccess('SivaAdmin');
  const queryClient = useQueryClient();

  const clusterEsaData = useQuery({
    queryKey: [SIVA_INNOVATION_FUNDS_KEY, clusterId, year],
    queryFn: () => sivaReportingService.getInnovationFunds(year, clusterId),
    retry: 1,
  });

  const upsertESAData = useMutation({
    mutationFn: (data: SivaInnovationFundsDto) => {
      if (!write) {
        throw new Error(tCommon('cannotWrite'));
      }

      return sivaReportingService.upsertInnovationFunds(year, data, clusterId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([SIVA_INNOVATION_FUNDS_KEY, clusterId, year]);
      queryClient.invalidateQueries([SIVA_INNOVATION_FUNDS_KEY, clusterId, year]);
    },
  });

  const sumPreInc = useMemo(() => {
    if (!clusterEsaData.data) return 0;
    return (
      (clusterEsaData.data.preIncOrdinary || 0) +
      (clusterEsaData.data.preIncOther || 0) +
      (clusterEsaData.data.preIncTop || 0) +
      (clusterEsaData.data.preIncTransferred || 0)
    );
  }, [clusterEsaData]);

  const sumOther = useMemo(() => {
    if (!clusterEsaData.data) return 0;
    return (
      (clusterEsaData.data.ordinary || 0) +
      (clusterEsaData.data.other || 0) +
      (clusterEsaData.data.top || 0) +
      (clusterEsaData.data.transferred || 0)
    );
  }, [clusterEsaData]);

  const sumAdmin = useMemo(() => {
    if (!clusterEsaData.data) return 0;
    return (
      (clusterEsaData.data.ordinaryOperationAndAdministration || 0) +
      (clusterEsaData.data.otherOperationAndAdministration || 0) +
      (clusterEsaData.data.topOperationAndAdministration || 0)
    );
  }, [clusterEsaData]);

  return {
    clusterEsaData: clusterEsaData.data,
    upsertESAData,
    clusterEsaQuery: clusterEsaData,
    sums: {
      preInc: sumPreInc,
      other: sumOther,
      administration: sumAdmin,
    },
  };
};
